import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from '../../util/reactIntl';
import { Avatar, Ratings, Button, IconCollection, VerificationBadges, NamedLink } from '../';

import css from './AboutOwner.module.css';
import classNames from 'classnames';
import { generateCitySlug, generateStoreSlug } from '../../util/seo';

const BIO_MAX_LENGTH = 180;

const truncate = (text, maxLength, isMobile) => {
  if (!text) return '';
  return text.length > maxLength ? text.slice(0, maxLength) + (isMobile ? '' : '…') : text;
};

const getBioMaxLength = (isMobile) => (isMobile ? 0 : 160);

// Bug fix (manual QA): mobile About Owner/Store card bio - a heuristic character
// count (~3 lines at this card's ~16px/24px-line-height text and column width),
// same style of estimate getBioMaxLength's own 160 already uses elsewhere in this
// file, since there's no live DOM to measure actual rendered line count against.
// Only gates whether the Read more button itself shows - the visual 3-line clamp
// is real CSS (-webkit-line-clamp, see .bioClamped), not this estimate.
const ORDER_PANEL_BIO_CLAMP_CHAR_ESTIMATE = 140;

const AboutOwner = ({
  usersEmailVerified,
  profile,
  listingsCount,
  reviews = [],
  rating = 5,
  verified,
  intl,
  variant,
  showOnlyReview = false,
  onClick = () => { },
  storeLink = null,
  notShowBio = false,
  customClassName = null,
  messageButtonLabel = null,
  authorLink = null,
  showVisitStore = true,
  showMessageButton = true,
  showHeading = variant === 'orderPanel' || variant === 'section',
  visitStoreButtonLabel = null,
  isOwnListing = false,
  messageButtonVariant = 'danger',
  ctaSize = null,
  showMessageIcon = true,
}) => {
  if (!profile) return null;

  const { isEmailVerified = false, isStripeVerified = false, isCompletedTransitions = false, isPhoneVerified = false } = usersEmailVerified?.user || {};
  const isToShowVerificationBadges = isEmailVerified || isStripeVerified || isCompletedTransitions || isPhoneVerified;
  // Dynamic mobile detection
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < 768
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const bio = profile?.attributes?.profile?.bio || '';
  const bioMaxLength = getBioMaxLength(isMobile);
  const shouldShowReadMore = bio && bio.length > bioMaxLength;
  const [readMore, setReadMore] = useState(isMobile ? true : (bio.length > bioMaxLength));
  // Bug fix (manual QA): separate from `readMore` above (that state is tied to the
  // non-orderPanel branches' truncate()-based logic) - starts collapsed (3-line
  // clamp) whenever the bio is long enough to need a Read more toggle at all.
  const [bioExpanded, setBioExpanded] = useState(false);
  const publicData = profile?.attributes?.profile?.publicData || {};
  const storeCitySlug = generateCitySlug(publicData?.storeAddress?.city || publicData?.city);
  const isStoreAccount = !!publicData?.storeName;
  const displayName =
    publicData?.storeName || profile?.attributes?.profile?.displayName || '';
  const memberSince = profile?.attributes?.createdAt
    ? new Date(profile.attributes.createdAt).getFullYear()
    : '';
  const avatarUser = profile;
  const reviewCount = reviews?.length;

  const listingsLabel = intl.formatMessage(
    { id: 'AboutOwner.listings' },
    { count: listingsCount }
  );
  const reviewsLabel = intl.formatMessage(
    { id: 'AboutOwner.reviews' },
    { count: reviewCount }
  );
  const verifiedLabel = intl.formatMessage({ id: 'AboutOwner.verified' });
  const defaultMessageOwnerLabel = intl.formatMessage({
    id: 'AboutOwner.messageOwner',
  });
  const visitStoreLabel = intl.formatMessage({ id: 'AboutOwner.visitStore' });

  const readMoreLabel = intl.formatMessage({ id: 'AboutOwner.readMore' });
  const readLessLabel = intl.formatMessage({ id: 'AboutOwner.readLess' });
  const readMoreCtaLabel = intl.formatMessage({ id: 'AboutOwner.readMoreCta' });
  const readLessCtaLabel = intl.formatMessage({ id: 'AboutOwner.readLessCta' });

  const finalMessageButtonLabel = messageButtonLabel || defaultMessageOwnerLabel;
  const finalVisitStoreLabel = visitStoreButtonLabel || visitStoreLabel;
  const isOrderPanelVariant = variant === 'orderPanel';
  const isSectionVariant = variant === 'section';

  const isIdVerified = !!publicData.isUserVerified;

  if (showOnlyReview) {
    return (
      <div
        className={css.onlyReview}
        aria-label={intl.formatMessage({ id: 'AboutOwner.scrollToReviews' })}
        role="button"
        tabIndex={0}
        onClick={() => {
          const reviewsElement = typeof document !== 'undefined' ? document.getElementById('reviews') : null;
          if (reviewsElement) {
            reviewsElement.scrollIntoView({ behavior: 'smooth' });
          }
        }}
      >
        <div className={css.review}>
          <Ratings rating={rating || 5} reviews={reviewCount} className={css.ratings} />
          <span className={css.count}>
            ({reviewCount} {reviewsLabel})
          </span>
        </div>
      </div>
    );
  }

  return (
    <section
      data-testid="about-owner-card"
      className={classNames(customClassName || css.panel, (variant === 'sm') && css.panelSM, isOrderPanelVariant && css.orderPanelVariant, isSectionVariant && css.sectionVariant)}
    >
      {showHeading && (
        <header>
          <h4 className={css.heading}>
            <FormattedMessage
              id={isStoreAccount ? 'AboutOwner.aboutTheStore' : 'AboutOwner.aboutTheOwner'}
              defaultMessage={isStoreAccount ? 'About The Store' : 'About The Owner'}
            />
          </h4>
        </header>
      )}

      <div className={css.avatarWrapper}>
        <Avatar
          user={avatarUser}
          renderSizes={isSectionVariant ? '84px' : '48px'}
          className={css.avatar}
          // verified={verified}
          showVerifiedBadge={isOrderPanelVariant}
        />
        <div className={css.ownerRightContent}>
          <div className={css.nameWithBadge}>
            {authorLink ? (
              <span className={css.name}>{authorLink}</span>
            ) : (
              <span className={css.name}>{displayName}</span>
            )}
            {verified && !isOrderPanelVariant && (
              isSectionVariant ? (
                <span className={css.plainVerifiedBadge} aria-label={verifiedLabel} role="img">
                  <i className="ri-verified-badge-fill" aria-hidden="true" />
                </span>
              ) : (
                <span className={css.badge}>
                  <IconCollection name="verifiedIcon" />
                  <span className={css.desktopLabel}>{verifiedLabel}</span>
                </span>
              )
            )}
          </div>
          <VerificationBadges
            isEmailVerified={isToShowVerificationBadges}
            usersEmailVerified={usersEmailVerified}
            isIdVerified={isIdVerified}
            className={css.verifiedBadges}
            badgeClassName={css.verificationBadge}
            emailBadgeClassName={css.verificationBadgeVerified}
            idBadgeClassName={css.verificationBadgeId}
          />

          {/* <div className={css.memberSince}>
            <FormattedMessage
              id="AboutOwner.memberSince"
              defaultMessage="Member since {year}"
              values={{ year: memberSince }}
            />
          </div> */}

          <div className={css.review}>
            <button type="button"
              aria-label={intl.formatMessage({ id: 'AboutOwner.scrollToReviews' })}
              style={{ cursor: 'pointer', border: 'none', background: 'none', padding: 0, margin: 0 }}
              onClick={(e) => {
                e.preventDefault();
                const reviewsElement = typeof document !== 'undefined' ? document.getElementById('reviews') : null;
                if (reviewsElement) {
                  reviewsElement.scrollIntoView({ behavior: 'smooth' });
                }
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Ratings rating={rating || 5} reviews={reviewCount} className={css.ratings} variant={variant} />
                <span className={css.count}>({reviewCount} {reviewsLabel})</span>
              </div>
            </button>
            <span className={css.divider}></span>
            {profile?.id?.uuid ? (
              <NamedLink
                name="ProfilePage"
                params={{
                  ...(storeCitySlug ? { city: storeCitySlug } : {}),
                  slug: generateStoreSlug(displayName),
                  id: profile.id.uuid
                }}
                style={{ cursor: 'pointer', border: 'none', background: 'none', padding: 0, margin: 0, textDecoration: 'none' }}
              >
                <span className={css.listings}>{listingsCount} {listingsLabel}</span>
              </NamedLink>
            ) : (
              <span className={css.listings}>{listingsCount} {listingsLabel}</span>
            )}
          </div>
        </div>
      </div>

      {/* Bug fix (manual QA): the mobile About Owner/Store card (variant="orderPanel")
          never rendered a bio at all - this whole section was gated out entirely by
          !isOrderPanelVariant below. Figma's own "The Owner" mock (node 17471:119812)
          shows a bio here, so this is a dedicated render path for that variant: a
          real 3-line CSS clamp (.bioClamped) rather than the other variants'
          character-count truncate(), since "3 lines" is a visual constraint that a
          character count can't reliably guarantee across word-wrap points. */}
      {!notShowBio && isOrderPanelVariant && bio && (
        <div className={css.bio}>
          <div className={classNames(!bioExpanded && css.bioClamped)}>
            {bio}
          </div>
          {bio.length > ORDER_PANEL_BIO_CLAMP_CHAR_ESTIMATE && (
            <div
              className={css.readMoreButton}
              onClick={() => setBioExpanded(expanded => !expanded)}
            >
              {bioExpanded ? readLessLabel : readMoreLabel}
              <span
                className={css.enhanceCollapseIcon}
                aria-hidden="true"
                style={{ transform: bioExpanded ? 'rotate(-180deg)' : 'none' }}
              >
                <IconCollection name={"read-more-arrow"} />
              </span>
            </div>
          )}
        </div>
      )}

      {!notShowBio && !isOrderPanelVariant && bio && (
        <>
          {shouldShowReadMore && !isSectionVariant ? (
            <div className={css.bio}>
              {readMore ? truncate(bio, bioMaxLength, isMobile) : bio}
              <div className={css.readMoreButton} onClick={() => setReadMore(!readMore)}>
                {readMore ? readMoreLabel : readLessLabel}
                <span
                  className={css.enhanceCollapseIcon}
                  aria-hidden="true"
                  style={{ transform: readMore ? 'none' : 'rotate(-180deg)' }}
                >
                  <IconCollection name={"read-more-arrow"} />
                </span>
              </div>
            </div>
          ) : (
            <div className={css.bio}>
              {isSectionVariant && shouldShowReadMore
                ? (readMore ? truncate(bio, bioMaxLength, isMobile) : bio)
                : bio}
            </div>
          )}
        </>
      )}

      {isSectionVariant ? (
        (shouldShowReadMore || showVisitStore) && (
          <div className={css.actionsRow}>
            {shouldShowReadMore ? (
              <button
                type="button"
                className={css.readMoreStandalone}
                onClick={() => setReadMore(!readMore)}
              >
                {readMore ? readMoreCtaLabel : readLessCtaLabel}
                <i
                  className="ri-arrow-down-s-line"
                  aria-hidden="true"
                  style={{ transform: readMore ? 'none' : 'rotate(180deg)' }}
                />
              </button>
            ) : <span />}

            {showVisitStore && (storeLink ? (
              storeLink
            ) : (
              <Button
                className={css.infoButton}
                size="sm"
                variant="info"
                arrow="right"
                icon={<IconCollection name="arrow_righ_stright" />}
              >
                {finalVisitStoreLabel}
              </Button>
            ))}
          </div>
        )
      ) : (
        (showMessageButton || showVisitStore) && (
          <div className={css.buttonGroup}>
            {showMessageButton && (
              <Button
                className={css.messageButton}
                size={ctaSize || ((variant === 'sm' || isOrderPanelVariant) ? 'sm' : 'md')}
                variant={messageButtonVariant}
                arrow={showMessageIcon ? 'left' : null}
                onClick={() => (!isOwnListing ? onClick(avatarUser) : null)}
                icon={showMessageIcon ? <i className="ri-message-3-line" /> : null}
              >
                {finalMessageButtonLabel}
              </Button>
            )}

            {showVisitStore && (storeLink ? (
              storeLink
            ) : (
              <Button
                className={css.infoButton}
                size={ctaSize || ((variant === 'sm' || isOrderPanelVariant) ? 'sm' : 'md')}
                variant="info"
                arrow="right"
                icon={<IconCollection name="arrow_righ_stright" />}
              >
                {finalVisitStoreLabel}
              </Button>
            ))}
          </div>
        )
      )}
    </section>
  );
};

AboutOwner.propTypes = {
  profile: PropTypes.object,
  listingsCount: PropTypes.number,
  reviews: PropTypes.array,
  rating: PropTypes.number,
  verified: PropTypes.bool,
  intl: PropTypes.object.isRequired,
  variant: PropTypes.string,
  showOnlyReview: PropTypes.bool,
  onClick: PropTypes.func,
  storeLink: PropTypes.node,
  notShowBio: PropTypes.bool,
  customClassName: PropTypes.string,
  messageButtonLabel: PropTypes.string,
  visitStoreButtonLabel: PropTypes.string,
  authorLink: PropTypes.node,
  showVisitStore: PropTypes.bool,
  showMessageButton: PropTypes.bool,
  showHeading: PropTypes.bool,
  isOwnListing: PropTypes.bool,
  messageButtonVariant: PropTypes.string,
  ctaSize: PropTypes.string,
  showMessageIcon: PropTypes.bool,
};

export default AboutOwner;
